import React, { useState, useRef } from "react";
import * as XLSX from "xlsx";
import { useUIContext } from "../../../context/UIContext";
import { useAuthContext } from "../../../context/AuthContext";
import { MdCloudUpload, MdDownload } from "react-icons/md";
import PreviewModal from "./components/PreviewModal";

/*
여기에 데이터만 넣으면 어디서든 사용 가능하도록 만든다.

1. 학생계정/기본정보 템플릿을 다운로드한다.
 - 학생계정/기본정보는 2개의 table이 합쳐진 형태로 1개의 템플릿으로 구성 되어있다.

2. 사용자가 정보를 입력 후 업로드를 한다. 

3. 업로드된 파일을 validate 한다.
 - 첫번째 줄 변경 안 된 것인지 확인
 - 두번째 줄 부터 컬럼별로 지정한 validate 규칙에 맞춰 데이터 이상없는 점검
 - 특히 username이 존재할 경우 해당 username 기반해서 row를 찾아서 다른 부분만 업데이트
 - username이 존재하지 않을 경우, 신규 추가
 - 이런 내용을 담아서 미리보기 제공
 - 한번의 올릴 수 있는 최대 크기 설정

4. 사용자가 미리보기 확인 후 실행 누르면 서버와 통신하여 업데이트 실행

5. 업데이트 실행결과 안내메시지 이후, 화면 reload



들어와야 하는 정보

1. SettingData/ExcelUploadConfig.js 에서 가져온다. 
페이지 경로 바탕으로 가져오든지 하자. 
 - validate

뭐가 들어가야할지는 아래 만들면 알게 되겠지.

*/

export default function UploadByExcel({
  config,
  onUploadComplete,
  disabled = false,
  existingData = [], // 기존 데이터 배열
  bulkUploadMutation, // 서버 통신을 위한 mutation 함수
}) {
  const { handleToastCenterTop } = useUIContext();
  const { user } = useAuthContext();
  const fileInputRef = useRef();

  const [previewData, setPreviewData] = useState([]);
  const [validationResults, setValidationResults] = useState(null);
  const [dragCounter, setDragCounter] = useState(0);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // 날짜 필드인지 확인하는 헬퍼 함수
  const isDateField = (column) => {
    return column.type === "date";
  };

  // 엑셀 날짜를 YYYY-MM-DD 문자열로 변환하는 헬퍼 함수
  const convertExcelDateToString = (value) => {
    if (!value) return null;

    // 이미 YYYY-MM-DD 형식이면 그대로 반환
    const stringValue = value.toString().trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(stringValue)) {
      return stringValue;
    }

    // 숫자인 경우 (엑셀 날짜)
    if (typeof value === "number" && value > 0) {
      try {
        // 엑셀 날짜를 JavaScript Date로 변환
        // 엑셀은 1900년 1월 1일을 기준으로 하고, JavaScript는 1970년 1월 1일을 기준
        const excelDate = new Date((value - 25569) * 86400 * 1000);

        // 유효한 날짜인지 확인
        if (isNaN(excelDate.getTime())) {
          return null;
        }

        // YYYY-MM-DD 형식으로 변환
        const year = excelDate.getFullYear();
        const month = String(excelDate.getMonth() + 1).padStart(2, "0");
        const day = String(excelDate.getDate()).padStart(2, "0");

        return `${year}-${month}-${day}`;
      } catch (error) {
        return null;
      }
    }

    // 그 외의 형식은 null 반환
    return null;
  };

  // 템플릿 다운로드 함수
  const handleDownloadTemplate = () => {
    try {
      let templateData = [];

      // templateSamples가 있으면 샘플 데이터 포함, 없으면 헤더만 있는 빈 템플릿
      if (config.templateSamples && config.templateSamples.length > 0) {
        // 샘플 데이터가 있는 경우
        templateData = config.templateSamples.map((sample) => {
          const row = {};
          config.columns.forEach((col) => {
            let value = sample[col.field] || "";

            // username 필드의 경우 실제 schoolid로 교체
            if (col.field === "username" && value && user?.schoolid) {
              value = value.replace(/@.*$/, `@${user.schoolid}`);
            }

            row[col.headerName] = value;
          });
          return row;
        });
      } else {
        // 샘플 데이터가 없는 경우: 헤더만 있는 빈 템플릿 생성
        const emptyRow = {};
        config.columns.forEach((col) => {
          emptyRow[col.headerName] = "";
        });
        templateData = [emptyRow];
      }

      // 엑셀 파일 생성
      const ws = XLSX.utils.json_to_sheet(templateData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, config.title);

      // 파일 다운로드
      const fileName = config.title + "_템플릿.xlsx";
      XLSX.writeFile(wb, fileName);

      handleToastCenterTop("템플릿이 다운로드되었습니다!");
    } catch (error) {
      console.error("템플릿 다운로드 오류:", error);
      handleToastCenterTop("템플릿 다운로드 중 오류가 발생했습니다.");
    }
  };

  // 엑셀 파일 처리
  const handleFileRead = (file) => {
    if (file.size > config.maxFileSize) {
      handleToastCenterTop(
        `파일 크기가 너무 큽니다. 최대 ${
          config.maxFileSize / 1024 / 1024
        }MB까지 가능합니다.`
      );
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        await processExcelData(jsonData);
      } catch (error) {
        handleToastCenterTop("엑셀 파일을 읽는 중 오류가 발생했습니다.");
        console.error(error);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // 필드 검증 함수
  const validateField = (
    column,
    value,
    rowNum,
    currentRowData = null,
    existingDataMap = null
  ) => {
    const errors = [];
    const cellRef = `${rowNum}행 ${column.headerName}`;

    // update 액션인지 미리 확인 (액션 결정 로직과 동일)
    let isUpdateAction = false;
    if (config.primaryKey && currentRowData) {
      const primaryKeyColumn = config.columns.find(
        (col) => col.field === config.primaryKey
      );
      if (primaryKeyColumn) {
        const primaryKeyValue =
          currentRowData[
            config.columns.findIndex((col) => col.field === config.primaryKey)
          ];
        if (
          primaryKeyValue &&
          existingDataMap.has(primaryKeyValue.toString().trim())
        ) {
          isUpdateAction = true;
        }
      }
    }

    // update 시 skip 옵션 체크
    if (isUpdateAction && column.onUpdate === "skip") {
      // update 시 해당 필드는 validation 건너뛰기
      return errors;
    }

    // 필수 체크
    if (column.required && (!value || value.toString().trim() === "")) {
      errors.push(`${cellRef}: 필수 입력 항목입니다`);
      return errors;
    }

    if (!value || value.toString().trim() === "") return errors;

    const strValue = value.toString().trim();

    // 길이 체크
    if (column.maxLength && strValue.length > column.maxLength) {
      errors.push(`${cellRef}: 최대 ${column.maxLength}자까지 입력 가능합니다`);
    }

    if (column.minLength && strValue.length < column.minLength) {
      errors.push(
        `${cellRef}: 최소 ${column.minLength}자 이상 입력해야 합니다`
      );
    }

    // username 필드에 대한 특별한 schoolid 검증
    if (column.field === "username") {
      const currentSchoolId = user?.schoolid;
      if (!currentSchoolId) {
        errors.push(`${cellRef}: 학교 정보를 확인할 수 없습니다`);
      } else if (!strValue.endsWith(`@${currentSchoolId}`)) {
        errors.push(
          `${cellRef}: 아이디는 '@${currentSchoolId}'로 끝나야 합니다 (예: student001@${currentSchoolId})`
        );
      }
    } else if (isDateField(column)) {
      // 날짜 필드는 특별 처리 (엑셀 숫자 날짜도 허용)
      const convertedDate = convertExcelDateToString(value);
      if (!convertedDate) {
        errors.push(
          `${cellRef}: 올바른 날짜 형식으로 입력해주세요 (YYYY-MM-DD 또는 엑셀 날짜)`
        );
      } else {
        // 변환된 날짜로 추가 패턴 검증 (있다면)
        if (
          column.validation?.pattern &&
          !column.validation.pattern.test(convertedDate)
        ) {
          errors.push(
            `${cellRef}: ${
              column.validation.message || "형식이 올바르지 않습니다"
            }`
          );
        }
      }
    } else {
      // 다른 필드들은 기존 패턴 검증
      if (
        column.validation?.pattern &&
        !column.validation.pattern.test(strValue)
      ) {
        errors.push(
          `${cellRef}: ${
            column.validation.message || "형식이 올바르지 않습니다"
          }`
        );
      }
    }

    // 옵션 검증 (성별 등)
    if (
      column.validation?.options &&
      !column.validation.options.includes(strValue)
    ) {
      errors.push(
        `${cellRef}: ${column.validation.options.join(
          ", "
        )} 중에서 선택해주세요`
      );
    }

    return errors;
  };

  // 데이터 검증 및 변환
  const processExcelData = async (rawData) => {
    if (rawData.length < 2) {
      handleToastCenterTop("데이터가 없습니다.");
      return;
    }

    // 헤더 검증
    const headerRow = rawData[0];
    const expectedHeaders = config.columns.map((col) => col.headerName);

    // 헤더 비교 (공백 제거하고 비교)
    const normalizeHeader = (header) =>
      header ? header.toString().trim() : "";

    const headerValid = expectedHeaders.every((expectedHeader, index) => {
      const actualHeader = normalizeHeader(headerRow[index]);
      const expected = normalizeHeader(expectedHeader);
      return actualHeader === expected;
    });

    if (!headerValid) {
      handleToastCenterTop(
        "템플릿의 첫 번째 줄이 변경되었습니다. 다시 다운로드해주세요."
      );
      return;
    }

    // 데이터 변환 및 검증
    const dataRows = rawData.slice(1);

    // 최대 행 수 체크
    if (dataRows.length > config.maxRows) {
      handleToastCenterTop(`최대 ${config.maxRows}행까지만 업로드 가능합니다.`);
      return;
    }

    const errors = [];
    const warnings = [];
    const processedData = [];
    let newCount = 0;
    let updateCount = 0;

    // unique 필드별로 중복 체크를 위한 Map
    const uniqueFields = new Map();
    config.columns
      .filter((col) => col.unique)
      .forEach((col) => {
        uniqueFields.set(col.field, new Set());
      });

    // 기존 데이터를 primaryKey 기준으로 Map 생성
    const existingDataMap = new Map();
    if (config.primaryKey && existingData.length > 0) {
      existingData.forEach((item) => {
        const keyValue = item[config.primaryKey];
        if (keyValue) {
          existingDataMap.set(keyValue, item);
        }
      });
    }

    for (let i = 0; i < dataRows.length; i++) {
      const rowData = dataRows[i];
      const rowNum = i + 2; // 엑셀 행 번호 (헤더 제외)

      // 빈 행 스킵
      if (rowData.every((cell) => !cell)) continue;

      const processedRow = {
        _rowNumber: rowNum,
        _action: "create",
      };

      // 설정에서 사용되는 모든 테이블 이름으로 빈 객체 초기화
      const tableNames = [...new Set(config.columns.map((col) => col.table))];
      tableNames.forEach((tableName) => {
        processedRow[tableName] = {};
      });

      let hasError = false;

      // 각 컬럼 검증
      config.columns.forEach((col, colIndex) => {
        const cellValue = rowData[colIndex];
        const fieldErrors = validateField(
          col,
          cellValue,
          rowNum,
          rowData,
          existingDataMap
        );

        if (fieldErrors.length > 0) {
          errors.push(...fieldErrors);
          hasError = true;
        }

        // 데이터 변환 및 할당
        let processedValue = cellValue;
        if (cellValue && cellValue.toString().trim() !== "") {
          // 날짜 필드면 엑셀 날짜 변환 적용
          if (isDateField(col)) {
            const convertedDate = convertExcelDateToString(cellValue);
            processedValue = convertedDate || cellValue.toString().trim();
          } else {
            processedValue = cellValue.toString().trim();
          }
        } else {
          processedValue = col.defaultValue || null;
        }

        // unique 필드 중복 체크
        if (col.unique && processedValue) {
          const uniqueSet = uniqueFields.get(col.field);
          if (uniqueSet.has(processedValue)) {
            errors.push(
              `${rowNum}행 ${col.headerName}: '${processedValue}' 값이 중복됩니다`
            );
            hasError = true;
          } else {
            uniqueSet.add(processedValue);
          }
        }

        // 테이블 객체가 존재하지 않으면 생성
        if (!processedRow[col.table]) {
          processedRow[col.table] = {};
        }

        processedRow[col.table][col.field] = processedValue;
      });

      // primaryKey 기준으로 액션 결정 (기존 데이터와 비교)
      if (config.primaryKey) {
        // primaryKey가 속한 테이블 찾기
        const primaryKeyColumn = config.columns.find(
          (col) => col.field === config.primaryKey
        );
        if (primaryKeyColumn && processedRow[primaryKeyColumn.table]) {
          const primaryKeyValue =
            processedRow[primaryKeyColumn.table][config.primaryKey];

          if (primaryKeyValue && existingDataMap.has(primaryKeyValue)) {
            processedRow._action = "update";
            updateCount++;
          } else {
            processedRow._action = "create";
            newCount++;
          }
        }
      } else {
        // primaryKey가 설정되지 않으면 모두 신규로 처리
        processedRow._action = "create";
        newCount++;
      }

      // update 액션일 때 onUpdate: "skip" 필드들 처리
      if (processedRow._action === "update") {
        config.columns.forEach((col) => {
          if (col.onUpdate === "skip") {
            const tableData = processedRow[col.table];
            if (tableData && col.field in tableData) {
              const fieldValue = tableData[col.field];
              if (!fieldValue || fieldValue.toString().trim() === "") {
                // 해당 필드가 비어있으면 제거 - 서버에서 기존 값 유지
                delete tableData[col.field];

                // 미리보기에서 표시하기 위한 플래그 (필드별로 설정)
                if (!processedRow._skippedFields) {
                  processedRow._skippedFields = [];
                }
                processedRow._skippedFields.push(col.field);
              }
            }
          }
        });
      }

      if (!hasError) {
        processedData.push(processedRow);
      }
    }

    const validationResults = {
      processedData,
      errors,
      warnings,
      summary: {
        total: processedData.length,
        new: newCount,
        update: updateCount,
        errors: errors.length,
        warnings: warnings.length,
      },
    };

    setPreviewData(processedData);
    setValidationResults(validationResults);

    // 모달 열기
    setShowPreviewModal(true);

    handleToastCenterTop(
      `${processedData.length}개 행을 처리했습니다. ${errors.length}개 오류`
    );
  };

  // 업로드 실행
  const handleUpload = async () => {
    if (!validationResults || validationResults.errors.length > 0) {
      handleToastCenterTop("오류를 먼저 수정해주세요.");
      return;
    }

    if (!bulkUploadMutation) {
      handleToastCenterTop("업로드 기능이 설정되지 않았습니다.");
      return;
    }

    setIsUploading(true);

    // 서버 통신용 데이터 준비
    const bulkData = {
      data: validationResults.processedData,
      primaryKey: config.primaryKey, // 기존 데이터 찾는 기준 필드
    };

    bulkUploadMutation.mutate(bulkData, {
      onSuccess: (result) => {
        // 성공 메시지 구성
        let successMessage = `처리 완료! 생성: ${
          result.created || 0
        }개, 수정: ${result.updated || 0}개`;

        // 실패한 레코드가 있으면 추가 정보 표시
        if (result.failed > 0) {
          successMessage += `, 실패: ${result.failed}개`;

          // failedRecords 상세 정보를 콘솔에 출력 (개발자 확인용)
          console.warn("실패한 레코드들:", result.failedRecords);

          // 사용자에게 실패 상세 알림
          const failedDetails = result.failedRecords
            .slice(0, 3) // 최대 3개까지만 표시
            .map((record) => `${record.rowNumber}행: ${record.reason}`)
            .join("\n");

          const additionalInfo =
            result.failedRecords.length > 3
              ? `\n... 외 ${result.failedRecords.length - 3}개 더`
              : "";

          setTimeout(() => {
            alert(
              `일부 데이터 처리에 실패했습니다:\n\n${failedDetails}${additionalInfo}\n\n자세한 내용은 개발자 도구(F12) 콘솔을 확인해주세요.`
            );
          }, 100);
        }

        handleToastCenterTop(successMessage);
        resetUploader();
        setIsUploading(false);

        // 부모 컴포넌트의 콜백 호출 (데이터 새로고침 등 처리)
        onUploadComplete?.(result);
      },
      onError: (error) => {
        console.error("업로드 오류:", error);
        handleToastCenterTop(
          `업로드 실패: ${error.message || "서버 오류가 발생했습니다."}`
        );
        setIsUploading(false);
      },
    });
  };

  const resetUploader = () => {
    setPreviewData([]);
    setValidationResults(null);
    setShowPreviewModal(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="excel-uploader mb-1 mt-1">
      <div
        className={`card border-2 border-dashed rounded p-1 text-center mb-1 ${
          dragCounter > 0 ? "border-primary bg-info" : "border-secondary"
        } ${disabled ? "opacity-50" : ""}`}
        onDragEnter={(e) => {
          e.preventDefault();
          if (!disabled) {
            setDragCounter((prev) => prev + 1);
          }
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setDragCounter((prev) => Math.max(0, prev - 1));
        }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          setDragCounter(0);
          if (!disabled && e.dataTransfer.files[0]) {
            handleFileRead(e.dataTransfer.files[0]);
          }
        }}
      >
        <div className="card-body pt-1 pb-1">
          {/* 파일 드롭존 */}
          <div>
            <div className="d-flex align-items-center justify-content-between">
              <MdCloudUpload
                size={25}
                className="text-muted me-2 vertical-align-middle"
              />
              <div className="me-2" style={{ verticalAlign: "middle" }}>
                일괄 업로드
              </div>
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={handleDownloadTemplate}
                disabled={disabled}
                style={{ width: "170px" }}
              >
                <MdDownload className="me-1" />
                양식 다운로드
              </button>
            </div>
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file && !disabled) handleFileRead(file);
                }}
                className="form-control"
                disabled={disabled}
              />
              <div style={{ fontSize: "12px" }} className="text-muted mt-1">
                엑셀 파일을 드래그하여 놓거나 클릭하여 선택하세요
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 미리보기 모달 */}
      <PreviewModal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        data={previewData}
        config={config}
        validationResults={validationResults}
        onConfirm={handleUpload}
        isUploading={isUploading}
      />
    </div>
  );
}
